import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Empty, List, Space, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { useState } from "react";

import { db } from "../lib/firebase";
import { ditColors } from "../lib/theme";

dayjs.extend(relativeTime);

export interface NotificationDoc {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  link?: string;
  read: boolean;
  createdAt?: string;
  sourceEvent?: string;
}

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  currentUserEmail: string | null;
  notifications?: NotificationDoc[];
}

export default function NotificationsPanel({
  open,
  onClose,
  currentUserEmail,
  notifications: notificationsProp,
}: NotificationsPanelProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationDoc[]>(
    notificationsProp ?? []
  );

  useEffect(() => {
    if (notificationsProp) {
      setNotifications(notificationsProp);
      return;
    }
    if (!currentUserEmail) {
      setNotifications([]);
      return;
    }
    const baseRef = collection(db, "notifications");
    const personalQuery = query(
      baseRef,
      where("userId", "==", currentUserEmail),
      orderBy("createdAt", "desc")
    );
    const broadcastQuery = query(
      baseRef,
      where("userId", "==", "broadcast"),
      orderBy("createdAt", "desc")
    );

    let personal: NotificationDoc[] = [];
    let broadcast: NotificationDoc[] = [];

    const merge = () => {
      const merged = [...personal, ...broadcast].sort((a, b) => {
        const aTs = a.createdAt ?? "";
        const bTs = b.createdAt ?? "";
        return bTs.localeCompare(aTs);
      });
      setNotifications(merged);
    };

    const unsubPersonal = onSnapshot(personalQuery, (snap) => {
      personal = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<NotificationDoc, "id">) }));
      merge();
    });
    const unsubBroadcast = onSnapshot(broadcastQuery, (snap) => {
      broadcast = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<NotificationDoc, "id">) }));
      merge();
    });
    return () => {
      unsubPersonal();
      unsubBroadcast();
    };
  }, [currentUserEmail, notificationsProp]);

  const unread = useMemo(() => notifications.filter((n) => !n.read), [notifications]);

  const markOneRead = async (id: string) => {
    try {
      await updateDoc(doc(db, "notifications", id), { read: true });
    } catch {
      // best-effort: a stale doc id is non-fatal here
    }
  };

  const markAllRead = async () => {
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, "notifications", n.id), { read: true }));
      await batch.commit();
    } catch {
      // emulator/no-network paths still surface unread state in UI
    }
  };

  const onItemClick = async (item: NotificationDoc) => {
    if (!item.read) {
      await markOneRead(item.id);
    }
    if (item.link) {
      navigate(item.link);
      onClose();
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Notifications"
      placement="right"
      width={400}
      zIndex={1100}
      aria-label="Notifications"
      extra={
        <Button onClick={markAllRead} disabled={unread.length === 0} size="small">
          Mark all read
        </Button>
      }
    >
      {notifications.length === 0 ? (
        <Empty description="You're all caught up!" />
      ) : (
        <div role="region" aria-label="Notification list">
          <List
            dataSource={notifications}
            renderItem={(item) => {
              const created = item.createdAt ? dayjs(item.createdAt) : null;
              const relative = created && created.isValid() ? created.fromNow() : "";
              const borderColor = item.read ? "transparent" : ditColors.accent;
              return (
                <List.Item
                  data-testid={`notification-${item.id}`}
                  onClick={() => onItemClick(item)}
                  style={{
                    cursor: "pointer",
                    borderLeft: `4px solid ${borderColor}`,
                    paddingLeft: 12,
                    background: item.read ? undefined : ditColors.secondaryLight,
                  }}
                  actions={
                    item.link
                      ? [
                          <Button
                            key="view"
                            type="link"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onItemClick(item);
                            }}
                          >
                            View
                          </Button>,
                        ]
                      : undefined
                  }
                >
                  <List.Item.Meta
                    title={
                      <Space size={8} align="center">
                        <Typography.Text strong={!item.read}>{item.title}</Typography.Text>
                        {relative && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {relative}
                          </Typography.Text>
                        )}
                      </Space>
                    }
                    description={
                      <Typography.Paragraph
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 0 }}
                      >
                        {item.body}
                      </Typography.Paragraph>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </Drawer>
  );
}
